import {
  FC,
  JSX,
  useEffect,
  useState
} from "react";
import {
  View,
  Text,
  Linking,
} from "react-native";
import { MaskedTextInput } from "react-native-mask-text";
import { useCreateSupportMessageMutation } from "@/_store/support/support.slice";
import { CLOSED_POSITION } from "@/_utils/contstans";
import CustomButton from "@/app/components/CustomButton";
import AlertIcon from "@/assets/icons/main/redAlert.svg";
import SuccessIcon from "@/assets/icons/main/greenAlert.svg";
import { toast } from "sonner-native";
import { usePlatformStyles } from "@/_hooks/usePlatformStyles";
import useFocus from "@/_hooks/useFocus";
import StyledTextInput from "@/app/components/StyledTextInput";
import debounce from "lodash/debounce";
import Container from "@/app/components/Container";
import { EdgeInsets, useSafeAreaInsets } from "react-native-safe-area-context";

interface IFeedbackProps {
  closeSupportForm: () => void;
  bottomSheetPosition: number;
}

export const labelBlockClass: string = "flex-row items-start";
export const labelClass: string = "mb-2";
export const requiredLabelClass: string = "text-red-500 mr-1";
export const textInputClass: string = "border border-gray-300 rounded-2xl py-3 px-4 mb-6 text-base";

const Feedback: FC<IFeedbackProps> = ({
  closeSupportForm,
  bottomSheetPosition,
}): JSX.Element => {
  const [createSupportMessage, { isLoading }] = useCreateSupportMessageMutation();

  const { webOutlineNone } = usePlatformStyles();
  const nameFieldFocus = useFocus();
  const messageFieldFocus = useFocus();

  const [name, setName] = useState<string>("");
  const [mobilePhone, setMobilePhone] = useState<string>("");
  const [text, setText] = useState<string>("");

  const resetForm = (): void => {
    setName("");
    setMobilePhone("");
    setText("");
  }

  useEffect(() => {
    if (bottomSheetPosition === CLOSED_POSITION) resetForm();
  }, [bottomSheetPosition]);

  const hasNoValues: boolean = !name || !mobilePhone || !text;
  const isButtonDisabled: boolean = hasNoValues || isLoading;
  const insets: EdgeInsets = useSafeAreaInsets();


  const handleSubmit = async (): Promise<void> => {
    try {
      if (hasNoValues) return;

      await createSupportMessage({ name, mobile_phone: mobilePhone, text }).unwrap();

      closeSupportForm();
      resetForm();

      toast.success("Сообщение отправлено. В ближайшее время с вами свяжется наш менеджер.", {
        icon: <SuccessIcon />,
      });
    } catch (err) {
      console.error("Send support message error:", err);

      toast.error("При отправке запроса произошла ошибка. Пожалуйста, попробуйте снова позже.", {
        icon: <AlertIcon />,
      });
    }
  };

  const handleCall = (): void => {
    const phoneNumber: string = "88007000425";
    Linking.openURL(`tel:${phoneNumber}`);
  };

  const updateMobilePhone = debounce((val: string) => setMobilePhone(val), 200);

  return (

    <Container style={{ paddingBottom: insets.bottom + 100 }} className="flex-1 p-5">
      <Text className="text-header">Обратная связь</Text>
      <Text className="mb-6 text-base text-gray">
        Если у вас есть вопросы или пожелания, напишите нам, и мы обязательно свяжемся с вами.
      </Text>

      <View className={labelBlockClass}>
        <Text className={requiredLabelClass}>*</Text>
        <Text className={labelClass}>Ваше имя</Text>
      </View>
      <StyledTextInput
        className={textInputClass}
        placeholder="Введите ваше имя"
        value={name}
        style={webOutlineNone}
        isFocused={nameFieldFocus.isFocused}
        onFocus={nameFieldFocus.onFocus}
        onBlur={nameFieldFocus.onBlur}
        onChangeText={setName}
      />

      <View className={labelBlockClass}>
        <Text className={requiredLabelClass}>*</Text>
        <Text className={labelClass}>Телефон для связи с вами</Text>
      </View>
      <MaskedTextInput
        mask="+7 (999) 999-99-99"
        className={textInputClass}
        placeholder="+7 999 000 0000"
        keyboardType="phone-pad"
        style={webOutlineNone}
        value={mobilePhone}
        onChangeText={updateMobilePhone}
      />

      <View className={labelBlockClass}>
        <Text className={requiredLabelClass}>*</Text>
        <Text className={labelClass}>Сообщение</Text>
      </View>
      <StyledTextInput
        className={`${textInputClass} !mb-1`}
        placeholder="Ваше сообщение"
        value={text}
        multiline
        numberOfLines={4}
        maxLength={2000}
        textAlignVertical="top"
        onChangeText={setText}
        style={webOutlineNone}
        isFocused={messageFieldFocus.isFocused}
        onFocus={messageFieldFocus.onFocus}
        onBlur={messageFieldFocus.onBlur}
      />
      <Text className="mb-6 text-right stats-text">{text?.length}/2000</Text>

      <CustomButton
        title="Отправить"
        type="primary"
        className="mb-6"
        onPress={handleSubmit}
        disabled={isButtonDisabled}
      />

      <Text className="mb-6 text-base">Или свяжитесь с нами:</Text>
      <CustomButton
        title="8 (800) 700-04-25"
        type="filled"
        onPress={handleCall}
      />
    </Container>
  );
};

export default Feedback;
